import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useAuthContext } from "../auth/AuthContext";

import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { Logo } from "../components/Logo";
import { AdvancedSettings } from "./AdvancedSettings"

import {
  AppBar,
  Toolbar,
  Box,
  Menu,
  MenuItem,
  Typography,
  Button,
  InputBase,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { Features, isEnabled } from "../utils/featureToggle";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  width: "100%",
  maxWidth: 300,
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

export const Header = () => {
  //const { user, signinRedirect, signoutRedirect } = useAuth();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loginAnchorEl, setLoginAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showAdvancedSettingsMenu, setShowAdvancedSettingsMenu] = React.useState<boolean>(false);
  const [showAdvancedSettingsDialog, setShowAdvancedSettingsDialog] = React.useState<boolean>(false);
  
  const open = Boolean(anchorEl);
  const loginOpen = Boolean(loginAnchorEl);
  const { t } = useTranslation("header");
  const { isAuthenticated, login, logout } = useAuthContext();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoginClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setLoginAnchorEl(event.currentTarget);
    setShowAdvancedSettingsMenu(event.altKey && event.shiftKey)
  };

  const handleLoginClose = () => {
    setLoginAnchorEl(null);
  };

  const handleAdvancedSettingsClick = () => {
    setLoginAnchorEl(null);
    setShowAdvancedSettingsDialog(true);  
  }

  const handleAdvancedSettingsClose = () => {
    setLoginAnchorEl(null);
    setAnchorEl(null);
    setShowAdvancedSettingsDialog(false);  
  }

  return (
    <>
      <AppBar position="static" elevation={2} sx={{ background: "linear-gradient(to right, #F83901, #FCE26F)" }}>
        <Toolbar>
          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
            <MenuItem component={RouterLink} to="/"><Logo /></MenuItem>

            <Box display="flex" alignItems="center" whiteSpace={"nowrap"}>
              <MenuItem onClick={handleClose} component={RouterLink} to="/customercases">{t("header:menu1.title")}</MenuItem>
              <MenuItem onClick={handleClose} component={RouterLink} to="/services">{t("header:menu2.assignmentTypes")}</MenuItem>
              <MenuItem onClick={handleClose} component={RouterLink} to="/skills">{t("header:menu2.skills")}</MenuItem>
              <Button color="inherit" onClick={handleClick} sx={{ textTransform: "none" }}>
                {t("header:menu2.title")}
              </Button>
              <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleClose} component={RouterLink} to="/about">{t("header:menu2.about")}</MenuItem>
                {isEnabled(Features.ShowContact) && (
                  <MenuItem onClick={handleClose} component={RouterLink} to="/contact">{t("header:menu2.contact")}</MenuItem>
                )}
              </Menu>
              {isEnabled(Features.ShowSearch) && (
                <Search>
                  <SearchIconWrapper>
                    <SearchIcon />
                  </SearchIconWrapper>
                  <StyledInputBase placeholder={t("header:search")} inputProps={{ 'aria-label': 'search' }} />
                </Search>
              )}
              <LanguageSwitcher />

              {isAuthenticated ? (
                <Button onClick={logout} color="inherit" sx={{ ml: 2 }}>
                  {t("header:logout")}
                </Button>
              ) : (
                <>
                  <Button onClick={handleLoginClick} color="inherit" sx={{ ml: 2 }}>
                  {t("header:login.title")}
                  </Button>
                  <Menu anchorEl={loginAnchorEl} open={loginOpen} onClose={handleLoginClose}>
                    <MenuItem onClick={login}>{t("header:login.employee")}</MenuItem>
                    <MenuItem onClick={login}>{t("header:login.customer")}</MenuItem>
                    {showAdvancedSettingsMenu && (
                      <MenuItem onClick={handleAdvancedSettingsClick}>{t("header:login.advanced")}</MenuItem>
                    )}
                  </Menu>
                </>
              )}
            </Box>
          </Box>  
        </Toolbar>
      </AppBar>

      {showAdvancedSettingsDialog && (
        <AdvancedSettings open={showAdvancedSettingsDialog} onClose={handleAdvancedSettingsClose} />
      )}
    </>
  );
};